/**
 * BillingTab — admin view of recent purchases + manual grants of tokens
 * or subscriptions to a specific user.
 *
 * The list is intentionally simple (read-only table), but the "grant"
 * forms are the main reason this exists: support team can fix a bad
 * charge or comp a user in seconds.
 */

import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Gift, Infinity as InfinityIcon, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { FierroIcon } from '@/components/icons/FierroIcon';
import { type Plan, formatPrice } from '@/features/billing/types';
import { useAdminUsers } from '@/features/admin/useAdmin';
import { api } from '@/lib/api';

interface AdminPurchase {
  id: number;
  user_id: number;
  user_email: string;
  plan_slug: string;
  amount_cents: number;
  currency: string;
  status: string;
  culqi_charge_id: string;
  created_at: string;
}

export function BillingTab() {
  const qc = useQueryClient();

  const purchasesQ = useQuery({
    queryKey: ['admin', 'billing', 'purchases'],
    queryFn: async () =>
      (await api.get<AdminPurchase[]>('/api/billing/admin/purchases?limit=100')).data,
  });

  const plansQ = useQuery({
    queryKey: ['billing', 'plans'],
    queryFn: async () => (await api.get<Plan[]>('/api/billing/plans')).data,
  });

  // Grant tokens form -------------------------------------------------------
  const usersQ = useAdminUsers('');
  const [tokensUserId, setTokensUserId] = useState<number | ''>('');
  const [tokensAmount, setTokensAmount] = useState(50);
  const [tokensNote, setTokensNote] = useState('');
  const [tokensMsg, setTokensMsg] = useState<string | null>(null);
  const grantTokens = useMutation({
    mutationFn: async () => {
      if (!tokensUserId) throw new Error('Selecciona un usuario.');
      const { data } = await api.post('/api/billing/admin/grant-tokens', {
        user_id: tokensUserId,
        amount: tokensAmount,
        note: tokensNote,
      });
      return data;
    },
    onSuccess: () => {
      setTokensMsg('Fierros otorgados.');
      setTokensNote('');
      qc.invalidateQueries({ queryKey: ['admin'] });
    },
    onError: (err: unknown) => {
      setTokensMsg(err instanceof Error ? err.message : 'Falló el otorgamiento.');
    },
  });

  // Grant subscription form -------------------------------------------------
  const [subUserId, setSubUserId] = useState<number | ''>('');
  const [subPlanId, setSubPlanId] = useState<number | ''>('');
  const [subNote, setSubNote] = useState('');
  const [subMsg, setSubMsg] = useState<string | null>(null);
  const grantSub = useMutation({
    mutationFn: async () => {
      if (!subUserId || !subPlanId) throw new Error('Selecciona usuario y plan.');
      const { data } = await api.post('/api/billing/admin/grant-subscription', {
        user_id: subUserId,
        plan_id: subPlanId,
        note: subNote,
      });
      return data;
    },
    onSuccess: () => {
      setSubMsg('Suscripción otorgada.');
      setSubNote('');
      qc.invalidateQueries({ queryKey: ['admin'] });
    },
    onError: (err: unknown) => {
      setSubMsg(err instanceof Error ? err.message : 'Falló el otorgamiento.');
    },
  });

  const purchases = purchasesQ.data ?? [];
  const plans = plansQ.data ?? [];
  const users = usersQ.data ?? [];
  const subscriptionPlans = plans.filter((p) => p.is_subscription);

  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1fr_320px]">
      <section>
        <div className="mb-4 flex items-center gap-3">
          <h2 className="text-lg" style={{ fontFamily: 'var(--font-display)', fontWeight: 700 }}>
            Compras recientes
          </h2>
          <span className="font-mono tabular text-xs" style={{ color: 'var(--text-muted)' }}>
            {purchases.length}
          </span>
        </div>
        <div
          className="overflow-hidden rounded-[var(--radius-md)] border"
          style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}
        >
          <table className="w-full text-left text-sm">
            <thead style={{ background: 'var(--surface-2)' }}>
              <tr className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                <Th>Fecha</Th>
                <Th>Usuario</Th>
                <Th>Plan</Th>
                <Th>Monto</Th>
                <Th>Estado</Th>
                <Th>Charge ID</Th>
              </tr>
            </thead>
            <tbody>
              {purchases.length === 0 && !purchasesQ.isLoading && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center" style={{ color: 'var(--text-muted)' }}>
                    Sin compras todavía.
                  </td>
                </tr>
              )}
              {purchases.map((p) => (
                <tr key={p.id} className="border-t" style={{ borderColor: 'var(--border-subtle)' }}>
                  <td className="px-4 py-2 font-mono tabular text-[12px]" style={{ color: 'var(--text-muted)' }}>
                    {p.created_at.slice(0, 19).replace('T', ' ')}
                  </td>
                  <td className="px-4 py-2">{p.user_email}</td>
                  <td className="px-4 py-2 font-mono tabular text-[12px]">{p.plan_slug}</td>
                  <td className="px-4 py-2 font-mono tabular">
                    {formatPrice(p.amount_cents, p.currency)}
                  </td>
                  <td className="px-4 py-2 text-[12px]">{p.status}</td>
                  <td className="px-4 py-2 font-mono tabular text-[10px]" style={{ color: 'var(--text-muted)' }}>
                    {p.culqi_charge_id}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="flex flex-col gap-4">
        {/* ---- Grant tokens ------------------------------------------------ */}
        <div
          className="rounded-[var(--radius-md)] border p-4"
          style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}
        >
          <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold">
            <Gift size={14} strokeWidth={1.8} style={{ color: 'var(--accent)' }} />
            Otorgar fierros
          </h3>
          <form
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              setTokensMsg(null);
              grantTokens.mutate();
            }}
            className="flex flex-col gap-2"
          >
            <select
              value={tokensUserId}
              onChange={(e) => setTokensUserId(e.currentTarget.value ? Number(e.currentTarget.value) : '')}
              className="h-9 rounded-[var(--radius-sm)] border bg-[var(--surface-2)] px-2 text-sm"
              style={{ borderColor: 'var(--border-default)' }}
            >
              <option value="">— Selecciona usuario —</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.email}
                </option>
              ))}
            </select>
            <div className="flex items-center gap-2">
              <FierroIcon size={14} />
              <input
                type="number"
                min={1}
                max={100000}
                value={tokensAmount}
                onChange={(e) => setTokensAmount(Number(e.currentTarget.value))}
                className="h-9 w-24 rounded-[var(--radius-sm)] border bg-[var(--surface-2)] px-2 text-sm"
                style={{ borderColor: 'var(--border-default)' }}
              />
              <span className="text-[11.5px]" style={{ color: 'var(--text-muted)' }}>
                fierros
              </span>
            </div>
            <input
              type="text"
              placeholder="Nota (opcional)"
              value={tokensNote}
              onChange={(e) => setTokensNote(e.currentTarget.value)}
              className="h-9 rounded-[var(--radius-sm)] border bg-[var(--surface-2)] px-2 text-sm"
              style={{ borderColor: 'var(--border-default)' }}
            />
            <Button variant="primary" size="sm" disabled={grantTokens.isPending}>
              {grantTokens.isPending ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <Gift size={12} strokeWidth={1.8} />
              )}
              Otorgar
            </Button>
            {tokensMsg && (
              <div
                className="rounded-[var(--radius-sm)] border p-2 text-[11.5px]"
                style={{
                  background: 'var(--surface-2)',
                  borderColor: 'var(--border-default)',
                  color: 'var(--text-secondary)',
                }}
              >
                {tokensMsg}
              </div>
            )}
          </form>
        </div>

        {/* ---- Grant subscription ----------------------------------------- */}
        <div
          className="rounded-[var(--radius-md)] border p-4"
          style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}
        >
          <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold">
            <InfinityIcon size={14} strokeWidth={2} style={{ color: 'var(--accent)' }} />
            Otorgar suscripción
          </h3>
          <form
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              setSubMsg(null);
              grantSub.mutate();
            }}
            className="flex flex-col gap-2"
          >
            <select
              value={subUserId}
              onChange={(e) => setSubUserId(e.currentTarget.value ? Number(e.currentTarget.value) : '')}
              className="h-9 rounded-[var(--radius-sm)] border bg-[var(--surface-2)] px-2 text-sm"
              style={{ borderColor: 'var(--border-default)' }}
            >
              <option value="">— Selecciona usuario —</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.email}
                </option>
              ))}
            </select>
            <select
              value={subPlanId}
              onChange={(e) => setSubPlanId(e.currentTarget.value ? Number(e.currentTarget.value) : '')}
              className="h-9 rounded-[var(--radius-sm)] border bg-[var(--surface-2)] px-2 text-sm"
              style={{ borderColor: 'var(--border-default)' }}
            >
              <option value="">— Selecciona plan —</option>
              {subscriptionPlans.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.duration_days}d)
                </option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Nota (opcional)"
              value={subNote}
              onChange={(e) => setSubNote(e.currentTarget.value)}
              className="h-9 rounded-[var(--radius-sm)] border bg-[var(--surface-2)] px-2 text-sm"
              style={{ borderColor: 'var(--border-default)' }}
            />
            <Button variant="primary" size="sm" disabled={grantSub.isPending}>
              {grantSub.isPending ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <CheckCircle2 size={12} strokeWidth={1.8} />
              )}
              Activar suscripción
            </Button>
            {subMsg && (
              <div
                className="rounded-[var(--radius-sm)] border p-2 text-[11.5px]"
                style={{
                  background: 'var(--surface-2)',
                  borderColor: 'var(--border-default)',
                  color: 'var(--text-secondary)',
                }}
              >
                {subMsg}
              </div>
            )}
          </form>
        </div>
      </section>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-4 py-2">{children}</th>;
}
